import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BookmarkIcon } from '@heroicons/react/24/outline';
import { signInWithRedirect } from 'aws-amplify/auth';
import logo4T from '../assets/4Thought-Technologies.png';

const API_URL = process.env.REACT_APP_API_URL;
const COGNITO_CLIENT_ID = process.env.REACT_APP_USER_POOL_CLIENT_ID;
const AWS_REGION = process.env.REACT_APP_AWS_REGION || 'us-west-2';

function PasswordStrengthBar({ password }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
  const colors = ['', 'bg-red-500', 'bg-orange-400', 'bg-yellow-400', 'bg-green-400', 'bg-green-600'];
  if (!password) return null;
  return (
    <div className="mt-1">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className={`h-1 flex-1 rounded ${i <= score ? colors[score] : 'bg-gray-200 dark:bg-gray-600'}`} />
        ))}
      </div>
      <p className={`text-xs ${score <= 2 ? 'text-red-500' : score <= 3 ? 'text-yellow-600' : 'text-green-600'}`}>
        {labels[score]}
      </p>
    </div>
  );
}

/**
 * Call RespondToAuthChallenge directly from the browser to Cognito.
 * The backend (acceptInvite GET) already called AdminInitiateAuth server-to-server
 * and returned the short-lived session token — the temporary password never
 * leaves the backend.
 */
async function respondToNewPasswordChallenge({ cognitoUsername, session, newPassword, preferredUsername }) {
  const challengeResponses = {
    USERNAME: cognitoUsername,
    NEW_PASSWORD: newPassword,
  };
  if (preferredUsername) {
    challengeResponses['userAttributes.preferred_username'] = preferredUsername;
  }

  const res = await fetch(`https://cognito-idp.${AWS_REGION}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
    },
    body: JSON.stringify({
      ClientId: COGNITO_CLIENT_ID,
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      Session: session,
      ChallengeResponses: challengeResponses,
    }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || data.__type || 'Failed to set password');
  }
}

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [username, setUsername] = useState('');
  const [state, setState] = useState('checking'); // checking | form | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [expired, setExpired] = useState(false);

  // Invite data returned by GET /invite/{token}
  const [inviteData, setInviteData] = useState(null); // { email, cognitoUsername, session }

  useEffect(() => {
    const checkToken = async () => {
      try {
        const res = await fetch(`${API_URL}/invite/${token}`, { method: 'GET' });
        const data = await res.json().catch(() => ({}));

        if (res.status === 410 || res.status === 404) {
          if (data.error?.code === 'already_used') {
            navigate('/', { replace: true });
            return;
          }
          setExpired(true);
          setState('error');
          setErrorMsg('This invitation link has expired or is invalid. You can request a new invite below.');
          return;
        }

        if (!res.ok) {
          setState('error');
          setErrorMsg('Unable to verify invitation. Please try again later.');
          return;
        }

        setInviteData(data);
        setState('form');
      } catch {
        setState('form'); // network error — let submit surface the real error
      }
    };
    checkToken();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (password.length < 8) {
      setErrorMsg('Password must be at least 8 characters.');
      return;
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
      setErrorMsg('Password must include uppercase, lowercase, a number, and a symbol.');
      return;
    }
    if (password !== confirm) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    if (username && username.length < 3) {
      setErrorMsg('Username must be at least 3 characters.');
      return;
    }

    setState('submitting');

    try {
      // Step 1: Complete the NEW_PASSWORD_REQUIRED challenge directly with Cognito
      // (browser → Cognito, password never touches the backend).
      await respondToNewPasswordChallenge({
        cognitoUsername: inviteData.cognitoUsername,
        session: inviteData.session,
        newPassword: password,
        preferredUsername: username || undefined,
      });

      // Step 2: Mark the invite token as claimed on the backend.
      await fetch(`${API_URL}/invite/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });

      // Step 3: Redirect through Cognito Hosted UI to complete sign-in via PKCE.
      // RespondToAuthChallenge tokens are SRP-style (no 'aud' claim) and cannot
      // be used for API calls. The user signs in with their new password on the
      // Cognito login page and returns with a proper OAuth access token.
      setState('success');
      signInWithRedirect();
    } catch (err) {
      console.error('AcceptInvite error:', err);
      const message = err?.message || '';
      if (message.includes('InvalidPasswordException') || message.toLowerCase().includes('password')) {
        setErrorMsg(message || 'Password does not meet requirements.');
      } else if (message.includes('UsernameExistsException') || message.includes('preferred_username')) {
        setErrorMsg('That username is already taken. Please choose a different one.');
      } else if (message.includes('NotAuthorizedException')) {
        setErrorMsg('The invitation session has expired. Please reload the page and try again.');
      } else {
        setErrorMsg('An unexpected error occurred. Please try again.');
      }
      setState('form');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <BookmarkIcon className="h-8 w-8 text-primary-600" />
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">BookMarker</span>
          </div>
          <h1 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Accept Your Invitation</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Set a password to activate your account</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
          {state === 'checking' && (
            <div className="text-center py-4 text-gray-400 dark:text-gray-500 text-sm">Verifying invitation…</div>
          )}

          {state === 'success' && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Account activated! Redirecting to sign in…</p>
            </div>
          )}

          {(state === 'form' || state === 'submitting') && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {inviteData?.email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={inviteData.email}
                    readOnly
                    className="w-full border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username <span className="font-normal text-gray-400 dark:text-gray-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value.trim())}
                  className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Choose a username to sign in with"
                  autoComplete="username"
                  disabled={state === 'submitting'}
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">If set, you can use this instead of your email to sign in.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New password</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                  disabled={state === 'submitting'}
                />
                <PasswordStrengthBar password={password} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                  required
                  disabled={state === 'submitting'}
                />
                {confirm && password !== confirm && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
              {errorMsg && state === 'form' && (
                <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">{errorMsg}</p>
              )}
              <button
                type="submit"
                disabled={state === 'submitting'}
                className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 font-medium transition-colors disabled:opacity-50"
              >
                {state === 'submitting' ? 'Activating account…' : 'Set password & activate account'}
              </button>
            </form>
          )}

          {state === 'error' && (
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-6">{errorMsg}</p>
              {expired && <RequestReinviteInline />}
              {!expired && (
                <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:underline text-sm">Go to sign in</Link>
              )}
            </div>
          )}
        </div>

        <div className="mt-8 text-center">
          <img src={logo4T} alt="4Thought Technologies" className="h-6 mx-auto opacity-60" />
        </div>
      </div>
    </div>
  );
}

function RequestReinviteInline() {
  const [email, setEmail] = useState('');
  const [reqState, setReqState] = useState('idle'); // idle | submitting | done | error
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleRequest = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) return;
    setReqState('submitting');
    try {
      await fetch(`${API_URL}/invite/request-reinvite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setReqState('done');
    } catch {
      setReqState('error');
    }
  };

  if (reqState === 'done') {
    return (
      <p className="text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2">
        If that email address has a pending invitation, an admin has been notified to send a new link.
      </p>
    );
  }

  return (
    <div className="text-left border-t border-gray-100 dark:border-gray-700 pt-5 mt-4">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Request a new invite link</p>
      <form onSubmit={handleRequest} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="flex-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          required
          disabled={reqState === 'submitting'}
        />
        <button
          type="submit"
          disabled={reqState === 'submitting' || !EMAIL_RE.test(email)}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {reqState === 'submitting' ? '…' : 'Request'}
        </button>
      </form>
      {reqState === 'error' && (
        <p className="text-xs text-red-500 dark:text-red-400 mt-1">Something went wrong. Please try again.</p>
      )}
    </div>
  );
}
